'use client';

import dayjs from 'dayjs';
import { useState } from 'react';

import { periodLabelOf, PERIOD_PRESET_DAYS } from 'src/constants/plan-period';

import PeriodRangePicker from 'src/components/cmui/PeriodRangePicker';

import { HubSelect } from './hub-select';

/** 달력으로 직접 고른 두 날짜 */
export type HubPeriodRange = { start: Date; end: Date };

/**
 * 허브의 **기간 고르개** — 프리셋(`PERIOD_PRESET_DAYS`) 한 벌 · 플랜 밖 칸은 자리를 남기고 자물쇠를 단다.
 *
 * 🔑 잠금 판정은 부르는 쪽이 `usePlanPeriodLimit` 에서 받아 넘긴다(공유 페이지는 늘 거짓을 넘긴다).
 * 🚨 업그레이드 안내는 **잠긴 칸을 고를 때만** 뜬다(`onLocked` → `guardPeriod`) — 고르개를 여는 클릭에는 아무것도 안 띄운다.
 * 🔑 기간은 늘 걸려 있다(「전체」가 없다) — 늘 걸린 조건으로 칠한다. 처음 값일 때 칠하지 않는 화면은 `active` 로 정한다.
 * 🔑 `custom` 을 주면 목록 맨 아래 「직접 선택…」이 선다 — 고르면 달력이 칸 밑에 열리고, 고른 기간은
 *   칸 글자(`rangeLabel`)에 날짜 범위로 적힌다. 칸 옆에 달력 단추를 따로 세우지 않는다.
 */
export function HubPeriodSelect({
  days,
  dayOptions = PERIOD_PRESET_DAYS,
  rangeLabel = '',
  prefix = '기간',
  labelSuffix = '',
  isPeriodLocked,
  guardPeriod,
  onChangeDays,
  custom,
  active = true,
}: {
  days: number;
  dayOptions?: readonly number[];
  /** 달력으로 직접 고른 기간의 이름 — 있으면 프리셋 대신 이것을 적는다 */
  rangeLabel?: string;
  prefix?: string;
  /** 접힌 칸 글자 뒤에 붙는 말 — 「 · 집행 중」처럼 기간이 안 걸리는 갈래를 함께 적을 때 */
  labelSuffix?: string;
  isPeriodLocked: (days: number) => boolean;
  /** 잠긴 칸이면 업그레이드를 안내하고 `true` */
  guardPeriod: (days: number) => boolean;
  onChangeDays: (days: number) => void;
  /** 「직접 선택…」 — 고른 두 날짜 · 바꾸는 함수 · 달력이 거슬러 갈 날 수 · 잠김(누르면 `onLocked`) */
  custom?: {
    range: HubPeriodRange | null;
    onChange: (range: HubPeriodRange | null) => void;
    lookbackDays?: number;
    locked?: boolean;
    onLocked?: () => void;
  };
  /** 걸린 조건으로 칠할지 — 기본은 늘(채널 성과는 처음 값에서 벗어났을 때만) */
  active?: boolean;
}) {
  /** 달력을 붙일 닻 — 접힌 칸의 뿌리(콜백 ref 라 렌더 중에 ref 를 읽지 않는다) */
  const [anchor, setAnchor] = useState<HTMLDivElement | null>(null);
  const [calendarOpen, setCalendarOpen] = useState(false);

  const select = (
    <HubSelect
      rootRef={setAnchor}
      hideEmpty
      active={active}
      prefix={prefix}
      value={rangeLabel ? '' : String(days)}
      empty={rangeLabel || periodLabelOf(days)}
      renderLabel={labelSuffix ? () => `${rangeLabel || periodLabelOf(days)}${labelSuffix}` : undefined}
      options={dayOptions.map((day) => ({
        value: String(day),
        label: periodLabelOf(day),
        locked: isPeriodLocked(day),
      }))}
      onChange={(next) => { const day = Number(next); if (!guardPeriod(day)) onChangeDays(day); }}
      onLocked={(next) => guardPeriod(Number(next))}
      extra={custom ? {
        label: '직접 선택…',
        icon: 'solar:calendar-linear',
        locked: custom.locked,
        onClick: () => {
          if (custom.locked) {
            custom.onLocked?.();
            return;
          }
          setCalendarOpen(true);
        },
      } : undefined}
    />
  );

  if (!custom) return select;
  return (
    <>
      {select}
      <PeriodRangePicker
        hideTrigger
        open={calendarOpen}
        anchorEl={anchor}
        restrictToRecentDays={custom.lookbackDays}
        onClose={() => setCalendarOpen(false)}
        value={custom.range ? [dayjs(custom.range.start), dayjs(custom.range.end)] : [null, null]}
        onChange={(picked: unknown[] | null) => {
          const start = picked?.[0];
          const end = picked?.[1];
          custom.onChange(start && end ? { start: dayjs(start as dayjs.ConfigType).toDate(), end: dayjs(end as dayjs.ConfigType).toDate() } : null);
          setCalendarOpen(false);
        }}
      />
    </>
  );
}
